import { useMutation } from "@tanstack/react-query";
import { type Api, logWorkoutProgress } from "api-client";
import { observer } from "mobx-react-lite";
import { type PropsWithChildren, useCallback, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";
import { useWorkoutEditStore } from "src/workouts/WorkoutEditContext";
import {
  type LogWorkoutProgressSnapshot,
  workoutItemSaveContext,
} from "src/workouts/WorkoutItemSetContext.shared";

export const LogSetProgressSaveProvider = observer(
  function LogSetProgressSaveProvider(
    props: PropsWithChildren & { workoutItemId: string },
  ) {
    const workoutEditStore = useWorkoutEditStore();
    const { withSaving } = useAuthenticatedNavigationContext();

    const mutation = useMutation({
      mutationFn: async (payload: Api.LogWorkoutProgress.Input) => {
        return await withSaving(
          logWorkoutProgress({
            path: payload.path,
            body: payload.body,
          }),
        );
      },
    });

    const debouncedMutate = useDebouncedCallback(() => {
      const changedData: LogWorkoutProgressSnapshot[] = workoutEditStore
        .getWorkoutItemSets(props.workoutItemId)
        .map((workoutSet) => {
          return {
            workoutSetId: workoutSet.id,
            completed: workoutSet.completed,
            attributeValues: workoutSet.attributeValues.map(
              (attributeValue) => {
                return {
                  id: attributeValue.id,
                  trackedValue: attributeValue.trackedValue,
                };
              },
            ),
          };
        });

      if (changedData.length === 0) {
        return;
      }

      mutation.mutate({
        path: {
          workoutId: workoutEditStore.workout.id,
        },
        body: {
          workoutSets: changedData,
        },
      });
    }, 300);

    useEffect(() => {
      return () => {
        debouncedMutate.flush();
      };
    }, [debouncedMutate]);

    const requestSave = useCallback(() => {
      debouncedMutate();
    }, [debouncedMutate]);

    return (
      <workoutItemSaveContext.Provider value={requestSave}>
        {props.children}
      </workoutItemSaveContext.Provider>
    );
  },
);
